'use client';

// Package Imports
import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import NumericInput from 'react-numeric-input';
import Image from 'next/image';
import Link from 'next/link';
import { v4 as uuidv4 } from 'uuid';

// Context Imports (still needed for token + dispatch)
import { useOrder } from '@/lib/context/orderContext';

// Constants
import { PlaceholderImageGrey } from '@/lib/constants/common';

// Functions
import { splitCaseItems } from '@/lib/functions/splitCaseItems';
import { formatCurrency } from '@/lib/functions/formatCurrency';
import { UpdateItemQuantityInCart } from '@/lib/functions/updateItemQuantityInCart';
import { RemoveItemFromCart } from '@/lib/functions/removeItemFromCart';

// Components
import StylizedButtonLink from '@/components/stylizedButtonLink';
import Toast from '@/components/toast';

// Styles
import styles from './cartItems.module.scss';

// Types
import type { OrderPayloadItem as BaseOrderPayloadItemType } from '@/lib/types/orderPayload';

interface ToastState {
  id: string;
  type: 'error' | 'success' | 'notice';
  message: string;
}

export interface OrderPayloadItemType extends BaseOrderPayloadItemType {
  label?: 'Case' | 'Individual';
  isSplit?: boolean;
  caseQuantity?: number;
  totalItemQuantity?: number;
}

interface CartItemsProps {
  items: OrderPayloadItemType[];
  refreshCart: (showLoader: boolean) => void;
}

export default function CartItems({ items, refreshCart }: CartItemsProps) {
  const [isClient, setIsClient] = useState(false);
  const [toasts, setToasts] = useState<ToastState[]>([]);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { state: orderState, dispatch: orderDispatch } = useOrder();

  useEffect(() => setIsClient(true), []);

  useEffect(
    () => () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    },
    []
  );

  const showToast = (type: 'error' | 'success' | 'notice', message: string) => {
    setToasts((prevToasts) => [...prevToasts, { id: uuidv4(), type, message }]);
  };

  const getRowRemoveKey = (item: OrderPayloadItemType) =>
    `${item.id}-${item.label ?? 'solo'}`;

  const handleDismiss = (id: string) => {
    setToasts((prevToasts) => prevToasts.filter((alert) => alert.id !== id));
  };

  /**
   * Handle quantity change for an item in the cart.
   */
  const handleQuantityChange = (item: OrderPayloadItemType, newQuantity: number | null) => {
    const rowKey = getRowRemoveKey(item);
    if (removingKey === rowKey) return;

    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      if (newQuantity === null) return;

      const { id, label, isSplit, pricing, quantity: rowQuantity, totalItemQuantity } = item;

      const isCaseRow = isSplit && label === 'Case';
      const caseSize = pricing.itemPricing.caseQuantity || 1;
      const desiredUnits = isCaseRow ? newQuantity * caseSize : newQuantity;
      const totalCurrentUnits = isSplit ? totalItemQuantity ?? rowQuantity : rowQuantity;
      const updatedTotalUnits = Math.max(1, totalCurrentUnits - rowQuantity + desiredUnits);

      UpdateItemQuantityInCart(id, updatedTotalUnits, orderState, orderDispatch).then((result) => {
        if (!result.success) {
          showToast('error', result.message || 'Unable to update item quantity.');
          return;
        }
        refreshCart(false);
      });
    }, 500);
  };

  /**
   * Handle the deletion of an item from the cart.
   */
  const handleDeleteItem = async (item: OrderPayloadItemType): Promise<void> => {
    const { id, quantity, isSplit, label, totalItemQuantity = 0 } = item;
    if (id == null || removingKey) return;

    const key = getRowRemoveKey(item);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    setRemovingKey(key);

    try {
      const isSplitRow = isSplit && (label === 'Case' || label === 'Individual');
      let removalSucceeded = false;

      if (isSplitRow) {
        const remainingQuantity = totalItemQuantity - quantity;

        if (remainingQuantity > 0) {
          const result = await UpdateItemQuantityInCart(id, remainingQuantity, orderState, orderDispatch);
          removalSucceeded = result.success;
          if (!result.success) {
            showToast('error', result.message || 'Unable to update item quantity.');
          }
        } else {
          const result = await RemoveItemFromCart(id, orderState, orderDispatch);
          removalSucceeded = result.success;
          if (!result.success) {
            showToast('error', result.message || 'Unable to remove item from cart.');
          }
        }
      } else {
        const result = await RemoveItemFromCart(id, orderState, orderDispatch);
        removalSucceeded = result.success;
        if (!result.success) {
          showToast('error', result.message || 'Unable to remove item from cart.');
        }
      }

      if (removalSucceeded) {
        refreshCart(false);
      }
    } finally {
      setRemovingKey(null);
    }
  };

  if (!isClient) {
    return <section className={styles.wrapper}></section>;
  }

  return (
    <section className={styles.wrapper}>
      <div className={styles.toasts}>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            type={toast.type}
            message={toast.message}
            onDismiss={() => handleDismiss(toast.id)}
            dismissible
            fadeOutTime={5000}
          />
        ))}
      </div>
      <table>
        <thead>
          <tr>
            <th className={styles.productRemove}></th>
            <th className={styles.productImage}></th>
            <th className={styles.productName}>Product</th>
            <th className={styles.productPrice}>Unit Price</th>
            <th className={styles.productQuantity}>Quantity</th>
            <th>Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {splitCaseItems(items).map((item) => {
            const itemPricing = item.pricing.itemPricing;
            const appliedPricing = item.pricing.appliedPricing;
            const discountType = appliedPricing?.discountType || null;
            const thumbnailImage = item.thumbnailImage || PlaceholderImageGrey;
            const url = item.url;
            const rowKey = `${item.id}-${item.label ?? 'solo'}-${item.isSplit ? 'split' : 'full'}`;
            const isRemoving = removingKey === getRowRemoveKey(item);

            return (
              <tr
                key={rowKey}
                className={classNames(isRemoving && styles.rowRemoving)}
                aria-busy={isRemoving}
              >
                <td className={styles.productRemove}>
                  {isRemoving ? (
                    <img src="/images/loader3.svg" alt="" />
                  ) : (
                    <button
                      className={styles.removeItem}
                      aria-label={`Remove ${item.productName} (${item.label || 'Item'})`}
                      disabled={!!removingKey}
                      onClick={() => void handleDeleteItem(item)}
                    >
                      &times;
                    </button>
                  )}
                </td>
                <td className={styles.productImage}>
                  {isRemoving ? (
                    <Image
                      src={thumbnailImage}
                      width={55}
                      height={55}
                      alt={item.productName}
                    />
                  ) : (
                    <Link href={url}>
                      <Image
                        src={thumbnailImage}
                        width={55}
                        height={55}
                        alt={item.productName}
                      />
                    </Link>
                  )}
                </td>
                <td className={styles.productName}>
                  {isRemoving ? (
                    <>
                      {item.productName}
                      {item.attributes.size && <span className="size">({item.attributes.size})</span>}
                    </>
                  ) : (
                    <>
                      <Link href={url}>{item.productName}</Link>
                      {item.attributes.size && <span className="size">({item.attributes.size})</span>}
                    </>
                  )}
                  <span className={styles.itemMeta}>
                    {item.label && (
                      <span className={styles.itemLabel}>
                        {item.label}
                        {item.isSplit && ' (Split)'}
                      </span>
                    )}
                  </span>
                </td>
                <td
                  className={classNames(
                    styles.productPrice,
                    (discountType == 'promo') && styles.promoPrice,
                    (discountType == 'keyclub') && styles.keyclubPrice,
                    (discountType == 'case') && styles.casePrice,
                  )}
                >
                  {itemPricing?.unitPrice !== itemPricing?.unitPriceBeforeDiscount ? (
                    <>
                      {formatCurrency(itemPricing?.unitPrice)}
                      {item.label === 'Case' && item.isSplit ? (
                        <>
                          <span className={styles.caseSuffix}> CASE</span>
                          <span className={styles.discountSubtotalPrice}>
                            <del>
                              {formatCurrency(itemPricing.casePricePerUnitBeforeDiscount)}
                            </del>
                            {formatCurrency(itemPricing.casePricePerUnit)} EACH
                          </span>
                        </>
                      ) : (
                        itemPricing?.unitPriceBeforeDiscount &&
                    itemPricing?.unitPrice !== itemPricing?.unitPriceBeforeDiscount ? (
                            <span className={styles.discountSubtotalPrice}>
                              <del>
                                {formatCurrency(itemPricing?.unitPriceBeforeDiscount)}
                              </del>
                            </span>
                          ) : (
                            <span className={styles.noDiscount}></span>
                          )
                      )}
                    </>
                  ) : (
                    formatCurrency(itemPricing?.unitPrice)
                  )}
                </td>
                <td className={styles.productQuantity}>
                  <label htmlFor={`quantity-${item.id}-${item.label || ''}`} className="screenReaderText">
                    Quantity for {item.productName} {item.label && `(${item.label})`}
                  </label>
                  <NumericInput
                    id={`quantity-${item.id}-${item.label || ''}`}
                    mobile
                    className={styles.formControl}
                    value={item?.caseQuantity || item?.quantity}
                    onChange={(val) => handleQuantityChange(item, val)}
                    min={1}
                    step={1}
                    disabled={isRemoving}
                  />
                </td>
                <td className={styles.productSubtotal}>
                  {formatCurrency(appliedPricing?.subtotal)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <StylizedButtonLink variant="secondary" size="mini" href="/all-products/">
        Return to Shop
      </StylizedButtonLink>
    </section>
  );
}
